import { ChangeDetectorRef, Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { ToggleComponent } from 'src/app/@core/components/ui/toggle/toggle.component';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { SanitizeHtmlPipe } from 'src/app/@shared/pipes/sanitizeHtml.pipe';
import { ToastService } from 'src/app/@core/services/toast.service';
import moment from 'moment';
import { IntegrationsService } from 'src/app/contract-management/services/integrations.service';

@Component({
  selector: 'app-database-connection',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, ButtonComponent, ToggleComponent, InputTextModule, PasswordModule],
  templateUrl: './database-connection.component.html',
  styleUrl: './database-connection.component.scss'
})
export class DatabaseConnectionComponent implements OnInit {
  @Input() integration!: {
    key: string;
    value: string;
    name: string;
    description: string;
    imgPath?: string;
    imgSvgPath?: string;
    connected?: boolean;
    data?: {
      connectionUrl?: string;
      host?: string;
      port?: string;
      database?: string;
      username?: string;
      password?: string;
    }
  }
  @Output() close = new EventEmitter<{ connected?: boolean; statusChanged?: boolean }>();

  databaseForm!: FormGroup;
  connectionUrl: string = '';
  dots = Array(5).fill(0);
  lastSynced: string = '';
  isConnecting = false;
  isDisconnecting = false;
  isConnectionUrlEditable = false;
  private isUpdatingFromUrl = false;
  private isUpdatingFromForm = false;

  constructor(
    private fb: FormBuilder,
    private toastService: ToastService,
    private integrationsService: IntegrationsService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.databaseForm = this.fb.group({
      host: [this.integration.data?.host || '', Validators.required],
      port: [this.integration.data?.port || '', Validators.required],
      database: [this.integration.data?.database || '', Validators.required],
      user: [this.integration.data?.username || '', Validators.required],
      password: [this.integration.data?.password || '']
    });

    this.databaseForm.valueChanges.subscribe((formValue) => {
      if (!this.isUpdatingFromUrl) {
        this.isUpdatingFromForm = true;
        this.updateConnectionUrlFromForm(formValue);
        this.isUpdatingFromForm = false;
      }
    });

    if (this.integration.data?.connectionUrl) {
      this.connectionUrl = this.integration.data.connectionUrl;
    } else if (this.integration.data?.host) {
      this.updateConnectionUrlFromForm(this.databaseForm.value);
    } else {
      this.setDefaultConnectionUrl();
    }
  }

  get databaseType(): string {
    return this.integration?.value || 'postgres';
  }

  setDefaultConnectionUrl() {
    const defaults = this.getDefaultPorts();
    const defaultPort = defaults[this.databaseType] || '';
    this.connectionUrl = this.getDefaultPlaceholder();
  }

  getDefaultPlaceholder(): string {
    const dbType = this.databaseType;
    switch (dbType) {
      case 'postgres':
        return 'postgresql://username:password@hostname:5432/database';
      case 'mysql':
        return 'mysql://username:password@hostname:3306/database';
      case 'sqlite':
        return 'sqlite:///path/to/database.db';
      case 'mongodb':
        return 'mongodb://username:password@hostname:27017/database';
      default:
        return 'username@hostname:port/database';
    }
  }

  getDefaultPorts(): Record<string, string> {
    return {
      'postgres': '5432',
      'mysql': '3306',
      'sqlite': '',
      'mongodb': '27017'
    };
  }

  updateConnectionUrlFromForm(formValue: any) {
    const dbType = this.databaseType;
    const currentUrl = this.connectionUrl || '';

    if (dbType === 'sqlite') {
      this.connectionUrl = formValue.database ? `sqlite:///${formValue.database}` : 'sqlite:///';
      return;
    }

    let existingParts = {
      user: '',
      password: '',
      host: '',
      port: '',
      database: '',
      isSrv: false
    };

    if (currentUrl && currentUrl.trim() !== '') {
      try {
        const isMongoSrv = currentUrl.includes('mongodb+srv://');
        if (isMongoSrv) {
          existingParts.isSrv = true;
          const urlPattern = /^mongodb\+srv:\/\/(?:([^:]+)(?::([^@]+))?@)?([^/]+)(?:\/(.+))?$/;
          const match = currentUrl.match(urlPattern);
          if (match) {
            existingParts = {
              user: match[1] ? decodeURIComponent(match[1]) : '',
              password: match[2] ? decodeURIComponent(match[2]) : '',
              host: match[3] || '',
              port: '27017',
              database: match[4] || '',
              isSrv: true
            };
          }
        } else {
          const protocol = dbType === 'postgres' ? 'postgresql' : dbType;
          const urlPattern = new RegExp(`^${protocol}://(?:([^:]+)(?::([^@]+))?@)?([^:/]+)(?::(\\d+))?(?:/(.+))?$`);
          const match = currentUrl.match(urlPattern);
          if (match) {
            existingParts = {
              user: match[1] ? decodeURIComponent(match[1]) : '',
              password: match[2] ? decodeURIComponent(match[2]) : '',
              host: match[3] || '',
              port: match[4] || '',
              database: match[5] || '',
              isSrv: false
            };
          }
        }
      } catch (error) {
        console.error('Error parsing existing URL:', error);
      }
    }

    const user = formValue.user !== null && formValue.user !== undefined && formValue.user !== ''
      ? formValue.user
      : existingParts.user;
    const password = formValue.password !== null && formValue.password !== undefined && formValue.password !== ''
      ? formValue.password
      : existingParts.password;
    const host = formValue.host !== null && formValue.host !== undefined && formValue.host !== ''
      ? formValue.host
      : existingParts.host;
    const port = formValue.port !== null && formValue.port !== undefined && formValue.port !== ''
      ? formValue.port
      : existingParts.port;
    const database = formValue.database !== null && formValue.database !== undefined && formValue.database !== ''
      ? formValue.database
      : existingParts.database;

    if (!host) {
      if (!user && !port && !database) {
        this.connectionUrl = '';
        return;
      }
    }

    const encodedUser = user ? encodeURIComponent(user) : '';
    const encodedPassword = password ? encodeURIComponent(password) : '';
    const passwordPart = encodedPassword ? `:${encodedPassword}` : '';
    const auth = encodedUser ? (passwordPart ? `${encodedUser}${passwordPart}@` : `${encodedUser}@`) : '';
    const databasePart = database ? `/${database}` : '';

    if (dbType === 'mongodb' && existingParts.isSrv) {
      this.connectionUrl = `mongodb+srv://${auth}${host}${databasePart}`;
    } else {
      const protocol = dbType === 'postgres' ? 'postgresql' : dbType;
      const portPart = port ? `:${port}` : '';
      this.connectionUrl = `${protocol}://${auth}${host}${portPart}${databasePart}`;
    }
  }

  buildConnectionString(): string {
    const formValue = this.databaseForm.value;
    const dbType = this.databaseType;
    const currentUrl = this.connectionUrl || '';

    if (!formValue.host && !formValue.port && !formValue.database && !formValue.user) {
      return '';
    }

    if (dbType === 'sqlite') {
      return formValue.database ? `sqlite:///${formValue.database}` : 'sqlite:///';
    }

    const isMongoSrv = currentUrl.includes('mongodb+srv://');
    const user = formValue.user || '';
    const encodedUser = user ? encodeURIComponent(user) : '';
    const encodedPassword = formValue.password ? encodeURIComponent(formValue.password) : '';
    const password = encodedPassword ? `:${encodedPassword}` : '';
    const auth = encodedUser ? (password ? `${encodedUser}${password}@` : `${encodedUser}@`) : '';
    const host = formValue.host || '';
    const database = formValue.database ? `/${formValue.database}` : '';

    if (!host) {
      return '';
    }

    if (dbType === 'mongodb' && isMongoSrv) {
      return `mongodb+srv://${auth}${host}${database}`;
    }

    const protocol = dbType === 'postgres' ? 'postgresql' : dbType;
    const port = formValue.port ? `:${formValue.port}` : '';
    return `${protocol}://${auth}${host}${port}${database}`;
  }

  onConnectionUrlChange(value: string) {
    if (this.isUpdatingFromForm) {
      return;
    }
    if (!value?.includes('://')) return;

    this.connectionUrl = value;
    this.parseConnectionString(value);
  }

  parseConnectionString(url: string) {
    if (!url || url.trim() === '') {
      this.databaseForm.patchValue({
        host: '',
        port: '',
        database: '',
        user: '',
        password: ''
      }, { emitEvent: false });
      return;
    }

    this.isUpdatingFromUrl = true;
    const dbType = this.databaseType;

    try {
      if (dbType === 'sqlite') {
        const sqliteMatch = url.match(/^sqlite:\/\/(?:\/)?(.*)$/);
        if (sqliteMatch) {
          const dbPath = sqliteMatch[1] || '';
          this.databaseForm.patchValue({
            host: '',
            port: '',
            database: dbPath,
            user: '',
            password: ''
          }, { emitEvent: false });
        }
      } else if (dbType === 'mongodb' && url.includes('mongodb+srv://')) {
        const srvPattern = /^mongodb\+srv:\/\/(?:([^:]+)(?::([^@]+))?@)?([^/]+)(?:\/(.+))?$/;
        const match = url.match(srvPattern);

        if (match) {
          const user = match[1] ? decodeURIComponent(match[1]) : '';
          const password = match[2] ? decodeURIComponent(match[2]) : '';
          const host = match[3] || '';
          const database = match[4] || '';

          this.databaseForm.patchValue({
            host: host,
            port: '27017',
            database: database,
            user: user,
            password: password
          }, { emitEvent: false });
        }
      } else {
        let protocol = dbType === 'postgres' ? 'postgresql' : dbType;
        const urlPattern = new RegExp(`^${protocol}://(?:([^:]+)(?::([^@]+))?@)?([^:/]+)(?::(\\d+))?(?:/(.+))?$`);
        const match = url.match(urlPattern);

        if (match) {
          const user = match[1] ? decodeURIComponent(match[1]) : '';
          const password = match[2] ? decodeURIComponent(match[2]) : '';
          const host = match[3] || '';
          const port = match[4] || '';
          const database = match[5] || '';

          this.databaseForm.patchValue({
            host: host,
            port: port,
            database: database,
            user: user,
            password: password
          }, { emitEvent: false });
        } else {
          try {
            const urlObj = new URL(url);
            this.databaseForm.patchValue({
              host: urlObj.hostname,
              port: urlObj.port || (dbType === 'mongodb' ? '' : this.getDefaultPorts()[dbType] || ''),
              database: urlObj.pathname?.replace('/', '') || '',
              user: urlObj.username || '',
              password: urlObj.password || ''
            }, { emitEvent: false });
          } catch (e) {
            const simplePattern = /^([^@]+)@([^:]+)(?::(\d+))?(?:\/(.+))?$/;
            const simpleMatch = url.match(simplePattern);

            if (simpleMatch) {
              const user = simpleMatch[1] || '';
              const host = simpleMatch[2] || '';
              const port = simpleMatch[3] || '';
              const database = simpleMatch[4] || '';

              this.databaseForm.patchValue({
                host: host,
                port: port,
                database: database,
                user: user,
                password: ''
              }, { emitEvent: false });
            }
          }
        }
      }
    } catch (error) {
      console.error('Error parsing connection string:', error);
    } finally {
      this.isUpdatingFromUrl = false;
    }
  }

  saveDatabaseSettings() {
    this.onConnect(true);
  }

  onConnect(save: boolean = false) {
    if (this.databaseForm.invalid) {
      this.databaseForm.markAllAsTouched();

      const missingFields = [];
      if (this.databaseForm.get('host')?.invalid) missingFields.push('Host');
      if (this.databaseForm.get('database')?.invalid) missingFields.push('Database');
      if (this.databaseForm.get('user')?.invalid) missingFields.push('User');
      console.log(missingFields);
      this.toastService.error(
        `Please fill in the following required fields: ${missingFields.join(', ')}`
      );
      return;
    }

    this.isConnecting = save ? false : true;

    this.integrationsService.addDatabaseConnection({
      provider: this.integration.value,
      connectionUrl: this.connectionUrl,
      host: this.databaseForm.value.host,
      port: this.databaseForm.value.port,
      database: this.databaseForm.value.database,
      username: this.databaseForm.value.user,
      password: this.databaseForm.value.password,
      save: save
    }).subscribe({
      next: (res: any) => {
        if (res.data.status) {
          this.toastService.success(`Database connection ${save ? 'saved' : 'added'} successfully`);
          this.integration.connected = true;
          this.isConnecting = false;
          this.close.emit({ connected: true });
        }
        else {
          this.toastService.error(res.data.error, `Failed to ${save ? 'save' : 'add'} database connection`);
          this.integration.connected = false;
          this.isConnecting = false;
        }
      },
      error: (err: any) => {
        this.toastService.error(`Failed to ${save ? 'save' : 'add'} database connection`);
        this.integration.connected = false;
        this.isConnecting = false;
        // this.close.emit({ connected: false });
      }
    });


  }

  onDisconnect() {
    this.isDisconnecting = true;
    this.integrationsService.disconnectDatabaseConnection({
      provider: this.integration.value,
    }).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.toastService.success('Database connection disconnected successfully');
          this.isDisconnecting = false;
          this.integration.connected = false;
          this.integration.data = undefined;
          this.connectionUrl = this.getDefaultPlaceholder();
          this.databaseForm.patchValue({
            host: '',
            port: this.getDefaultPorts()[this.databaseType] || '',
            database: '',
            user: '',
            password: ''
          });
          this.cdr.detectChanges();
          this.close.emit({ connected: false, statusChanged: true });
        }
      },
      error: (err: any) => {
        this.toastService.error('Failed to disconnect database connection');
        this.isDisconnecting = false;
        this.integration.connected = true;
      }
    });
  }

  onClose() {
    this.close.emit();
  }

  toggleConnectionUrlEdit() {
    this.isConnectionUrlEditable = !this.isConnectionUrlEditable;
  }
}
